import React, { FC, Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import { Typography, Switch, Box, Grid } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { toggleTransportSettingTemplate } from 'apollo/templates/organization';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { labSettingsTemplate } from 'apollo/templates/organization/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import WaybillPrintSettings from './waybill-print-settings/waybill-print-settings.component';
import WaybillPackageSizes from './waybill-package-sizes/waybill-package-sizes.component';

const TransportServiceSettings: FC = () => {
    const { t: translate } = useTranslation(['lab-settings']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [isTransportEnable, setTransportEnable] = useState(false);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const currentOrganisation = currentOrgVar();
    const [printFormat, setPrintFormat] = useState<string>('');

    const hasNshift = currentOrganisation.transportSettings?.nShiftTransport?.isEnabled || false;

    const { loading } = useQuery(labSettingsTemplate, {
        onError(error) {
            showBoundary(error);
        },
        onCompleted({ currentLab }) {
            setTransportEnable(currentLab.isTransportEnable as boolean);
            setPrintFormat(currentLab.transportSettings?.nShiftTransport?.waybillPrintFormat);
        },
    });

    const [toggleTransportSetting] = useMutation(toggleTransportSettingTemplate, {
        onCompleted: ({ toggleTransportSetting }) => {
            currentOrganisation.isTransportEnable = toggleTransportSetting.isTransportEnable;
            currentOrgVar(currentOrganisation);
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('common:CHANGES_SAVED'),
            });
        },
    });

    const handleTransportEnableChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        setTransportEnable(isChecked);
        toggleTransportSetting({
            variables: {
                isTransportEnable: isChecked,
            },
        });
    };

    return (
        <Fragment>
            <PageTitle headingStyle="h2" title={translate('lab-settings:TRANSPORT_SERVICES')} />
            <Grid container>
                <Grid item sm={12} md={8}>
                    {!loading ? (
                        <Fragment>
                            {hasNshift ? (
                                <Fragment>
                                    <PageTitle headingStyle="h3" title={translate('lab-settings:WAYBILL_PRINT')} />
                                    <Box mb={5}>
                                        <WaybillPrintSettings currentPrintFormat={printFormat} />
                                    </Box>
                                    <PageTitle
                                        headingStyle="h3"
                                        title={translate('lab-settings:WAYBILL_PACKAGE_SIZES')}
                                    />
                                    <Box mb={5}>
                                        <WaybillPackageSizes />
                                    </Box>
                                </Fragment>
                            ) : undefined}
                            <PageTitle headingStyle="h3" title={translate('lab-settings:DENTHUB_TRANSPORT_SERVICES')} />
                            <Box display="flex" alignItems="center">
                                <Switch
                                    color="primary"
                                    size="medium"
                                    checked={isTransportEnable}
                                    onChange={handleTransportEnableChange}
                                />
                                <Typography variant="body2" sx={{ marginLeft: 2 }}>
                                    {translate('lab-settings:TRANSPORT_INFORMATION')}
                                </Typography>
                            </Box>
                        </Fragment>
                    ) : undefined}
                </Grid>
            </Grid>
        </Fragment>
    );
};

export default TransportServiceSettings;
